import { useEffect, useState } from "react";
import SEO from "@/components/SEO";
import { canonical, webPageSchema } from "@/utils/seo";

const requirements = [
  {
    icon: "ri-heart-pulse-line",
    title: "熱愛探索",
    description: "對各種活動體驗充滿好奇心，願意嘗試不同類型的服務。",
  },
  {
    icon: "ri-edit-line",
    title: "觀察力敏銳",
    description: "能夠注意到細節，並用客觀的角度評估體驗的各個面向。",
  },
  {
    icon: "ri-time-line",
    title: "時間彈性",
    description: "每個月至少能安排 2-3 次體驗行程，並在一週內完成評測報告。",
  },
  {
    icon: "ri-shield-check-line",
    title: "保密承諾",
    description: "體驗過程中不得向店家透露神秘客身份，確保評比的真實性。",
  },
];

const processSteps = [
  { num: "01", title: "填寫申請表", desc: "提交基本資料與你感興趣的體驗類別" },
  { num: "02", title: "線上面試", desc: "我們會進行簡短視訊面試，了解你的背景與動機" },
  { num: "03", title: "培訓課程", desc: "通過面試後，參加 2 小時的神秘客培訓工作坊" },
  { num: "04", title: "開始體驗", desc: "正式成為神秘客，開始你的探索之旅" },
];

export default function JoinUsPage() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  return (
    <div className="w-full">
      <SEO
        meta={{
          title: "加入成為神秘客｜美好時光指南",
          description: "免費體驗各種精彩活動，還能幫助更多人找到好店家。加入美好時光指南神秘客團隊。",
          canonical: canonical("/join-us"),
          keywords: ["神秘客", "加入我們", "體驗活動", "美好時光指南"],
          structuredData: webPageSchema("加入成為神秘客｜美好時光指南", "免費體驗各種精彩活動，加入美好時光指南神秘客團隊。", "/join-us"),
        }}
      />

      {/* Hero */}
      <div className="w-full bg-slate-50 border-b border-slate-100">
        <div className="max-w-4xl mx-auto px-4 md:px-8 py-12 md:py-20 text-center">
          <div className="w-16 h-16 flex items-center justify-center rounded-full bg-primary/10 text-primary mx-auto mb-6">
            <i className="ri-user-star-line text-3xl" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4">
            加入神秘客，免費體驗精彩活動
          </h1>
          <p className="text-slate-500 max-w-2xl mx-auto mb-8">
            美好時光指南的神秘客是一群熱愛體驗、觀察力敏銳的夥伴。你將免費體驗各種精彩活動，並透過你的評測，幫助數十萬讀者找到值得信賴的好店家。
          </p>
          <a
            href="mailto:mysteryshopper@goldentimeguide.com?subject=神秘客申請"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-white font-medium rounded-full hover:bg-primary-dark transition-colors whitespace-nowrap"
          >
            <i className="ri-mail-send-line" />
            立即申請
          </a>
        </div>
      </div>

      {/* Benefits */}
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-12 md:py-16">
        <div
          className={`transition-all duration-700 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`}
        >
          <h2 className="text-2xl font-bold text-slate-800 text-center mb-10">
            成為神秘客的好處
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: "ri-ticket-line", title: "免費體驗", desc: "每月獲得 2-3 次免費體驗機會，涵蓋按摩、美容、美甲、美髮等美業類別。" },
              { icon: "ri-vip-crown-line", title: "優先體驗", desc: "新開幕的熱門店家與限定活動，神秘客享有優先預約權。" },
              { icon: "ri-community-line", title: "社群歸屬", desc: "加入神秘客專屬社群，與志同道合的夥伴交流心得、分享趣事。" },
              { icon: "ri-award-line", title: "成就系統", desc: "累積評測數量與品質積分，解鎖專屬徽章與進階體驗資格。" },
              { icon: "ri-gift-line", title: "額外獎勵", desc: "年度優秀神秘客可獲得獎金、禮品卡或海外體驗之旅。" },
              { icon: "ri-profile-line", title: "履歷加分", desc: "神秘客經驗是體驗經濟領域的獨特履歷亮點，提升個人品牌價值。" },
            ].map((b) => (
              <div
                key={b.title}
                className="bg-white rounded-xl p-6 border border-slate-100 hover:border-primary/20 transition-all"
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary/10 text-primary mb-4">
                  <i className={`${b.icon} text-xl`} />
                </div>
                <h3 className="text-base font-bold text-slate-800 mb-2">
                  {b.title}
                </h3>
                <p className="text-sm text-slate-500 leading-relaxed">
                  {b.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Requirements */}
      <div className="w-full bg-slate-50">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-12 md:py-16">
          <h2 className="text-2xl font-bold text-slate-800 text-center mb-10">
            神秘客條件
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {requirements.map((r) => (
              <div
                key={r.title}
                className="bg-white rounded-xl p-6 border border-slate-100 text-center"
              >
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary/10 text-primary mx-auto mb-4">
                  <i className={`${r.icon} text-xl`} />
                </div>
                <h3 className="text-base font-bold text-slate-800 mb-2">
                  {r.title}
                </h3>
                <p className="text-sm text-slate-500 leading-relaxed">
                  {r.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Process */}
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-12 md:py-16">
        <h2 className="text-2xl font-bold text-slate-800 text-center mb-10">
          申請流程
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {processSteps.map((step) => (
            <div key={step.num} className="relative">
              <div className="bg-white rounded-xl p-6 border border-slate-100 text-center">
                <span className="inline-block text-3xl font-black text-primary/20 mb-3">
                  {step.num}
                </span>
                <h3 className="text-base font-bold text-slate-800 mb-2">
                  {step.title}
                </h3>
                <p className="text-sm text-slate-500">{step.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* CTA */}
      <div className="w-full bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 md:px-8 py-12 md:py-16 text-center">
          <h2 className="text-2xl font-bold text-slate-800 mb-3">
            準備好開始你的神秘客之旅了嗎？
          </h2>
          <p className="text-slate-500 mb-6">
            點擊下方按鈕發送申請郵件，我們會在 3 個工作日內回覆你。
          </p>
          <a
            href="mailto:mysteryshopper@goldentimeguide.com?subject=神秘客申請"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-white font-medium rounded-full hover:bg-primary-dark transition-colors whitespace-nowrap"
          >
            <i className="ri-mail-send-line" />
            立即申請
          </a>
        </div>
      </div>
    </div>
  );
}